<template>
  <section class="c2c-tsx" :aria-label="t('transcode.aria_label')">
    <div class="c2c-tsx-head">
      <h3>{{ t('transcode.heading') }}</h3>
      <button
        type="button"
        class="c2c-tsx-refresh"
        :disabled="loading"
        :aria-label="t('transcode.refresh_aria')"
        @click="poll"
      >
        <RefreshCw :size="16" />
        {{ loading ? t('transcode.refreshing') : t('transcode.refresh') }}
      </button>
    </div>

    <p v-if="lastError" class="c2c-tsx-error">{{ lastError }}</p>

    <p v-else-if="!hasFetched && loading" class="c2c-tsx-empty">
      {{ t('transcode.loading_first') }}
    </p>

    <p v-else-if="hasFetched && entries.length === 0" class="c2c-tsx-empty">
      {{ t('transcode.no_entries_yet') }}
    </p>

    <ul v-else class="c2c-tsx-list">
      <li v-for="entry in entries" :key="entry.key" class="c2c-tsx-item">
        <span class="c2c-tsx-key">{{ entry.key }}</span>
        <span class="c2c-tsx-status" :class="statusClass(entry.status)">
          <span class="c2c-tsx-dot" :class="statusClass(entry.status)" aria-hidden="true"></span>
          {{ statusLabel(entry) }}
        </span>
      </li>
    </ul>

    <p class="c2c-tsx-foot">
      <span v-if="allDone">{{ t('transcode.all_done') }}</span>
      <span v-else-if="hasFetched">
        {{ t('transcode.next_check', { seconds: nextCheckIn }) }}
      </span>
    </p>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue"
import { RefreshCw } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { getTranscodeStatus } from "@/lib/transcode-status"
import type { TranscodeEntry, TranscodeJobStatus } from "@/lib/types"

const { t } = useI18n()

const props = defineProps<{ filename: string }>()

const POLL_INTERVAL_SECONDS = 30
// Stop polling after ~30 minutes - transcoding rarely takes longer for files
// inside our 1 GB / one hour ceiling. The user can still hit "Refresh"
// manually.
const MAX_POLL_DURATION_SECONDS = 30 * 60

const entries = ref<TranscodeEntry[]>([])
const allDone = ref(false)
const lastError = ref<string | null>(null)
const loading = ref(false)
const hasFetched = ref(false)
const nextCheckIn = ref(POLL_INTERVAL_SECONDS)

let pollTimer: number | undefined
let countdownTimer: number | undefined
let pollingStartedAt = 0

async function poll() {
  if (loading.value) return
  loading.value = true
  lastError.value = null
  try {
    const result = await getTranscodeStatus(props.filename)
    entries.value = result.entries
    allDone.value = result.allDone
    hasFetched.value = true
  } catch (err) {
    lastError.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
    nextCheckIn.value = POLL_INTERVAL_SECONDS
  }
}

function scheduleNextPoll() {
  clearTimeout(pollTimer)
  if (allDone.value) return
  if ((Date.now() - pollingStartedAt) / 1000 > MAX_POLL_DURATION_SECONDS) return
  pollTimer = window.setTimeout(async () => {
    await poll()
    scheduleNextPoll()
  }, POLL_INTERVAL_SECONDS * 1000)
}

function statusClass(status: TranscodeJobStatus): string {
  switch (status) {
    case "queued": return "c2c-tsx--queued"
    case "transcoding": return "c2c-tsx--running"
    case "completed": return "c2c-tsx--done"
    case "failed": return "c2c-tsx--failed"
  }
}

function statusLabel(entry: TranscodeEntry): string {
  switch (entry.status) {
    case "queued": return t('transcode.status_queued')
    case "transcoding":
      return entry.elapsedSeconds != null
        ? t('transcode.status_running_with_time', { seconds: entry.elapsedSeconds })
        : t('transcode.status_running')
    case "completed":
      return entry.elapsedSeconds != null
        ? t('transcode.status_done_in', { seconds: entry.elapsedSeconds })
        : t('transcode.status_done')
    case "failed":
      return entry.error
        ? t('transcode.status_failed_with_msg', { msg: entry.error })
        : t('transcode.status_failed')
  }
}

onMounted(async () => {
  pollingStartedAt = Date.now()
  // Slight initial delay: Commons needs ~1-2s for the file to register.
  setTimeout(async () => {
    await poll()
    scheduleNextPoll()
  }, 1500)

  // Live countdown display so the user knows another check is coming.
  countdownTimer = window.setInterval(() => {
    if (nextCheckIn.value > 0 && !loading.value && !allDone.value) {
      nextCheckIn.value = Math.max(0, nextCheckIn.value - 1)
    }
  }, 1000)
})

onUnmounted(() => {
  clearTimeout(pollTimer)
  clearInterval(countdownTimer)
})
</script>

<style scoped>
.c2c-tsx {
  margin-top: 16px; padding: 16px;
  background: white; border: 1px solid #eaecf0; border-radius: 4px;
  text-align: start;
}
.c2c-tsx-head {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 12px; gap: 12px;
}
.c2c-tsx-head h3 { margin: 0; font-size: 14px; color: #54595d; }
.c2c-tsx-refresh {
  display: inline-flex; align-items: center; gap: 4px;
  background: none; border: 1px solid #a2a9b1; border-radius: 3px;
  padding: 3px 10px; font-size: 12px; cursor: pointer;
  color: #202122;
}
.c2c-tsx-refresh:hover:not(:disabled) { background: #eaecf0; }
.c2c-tsx-refresh:disabled { opacity: 0.5; cursor: wait; }
.c2c-tsx-error {
  font-size: 12px; color: #b32424;
  padding: 8px; background: #fee7e6; border-radius: 3px; margin: 0;
}
.c2c-tsx-empty {
  font-size: 12px; color: #54595d; margin: 0; padding: 8px 0;
}
.c2c-tsx-list { list-style: none; margin: 0; padding: 0; }
.c2c-tsx-item {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0; border-bottom: 1px solid #eaecf0; gap: 8px;
}
.c2c-tsx-item:last-child { border-bottom: none; }
.c2c-tsx-key {
  font-family: ui-monospace, monospace; font-size: 12px;
  color: #202122; word-break: break-all;
}
.c2c-tsx-status {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; white-space: nowrap;
}
.c2c-tsx-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.c2c-tsx--queued.c2c-tsx-dot { background: #c8ccd1; }
.c2c-tsx--running.c2c-tsx-dot { background: #3366cc; animation: c2c-pulse 1.6s ease-in-out infinite; }
.c2c-tsx-status.c2c-tsx--running { color: #2a4b8d; }
.c2c-tsx--done.c2c-tsx-dot { background: #14866d; }
.c2c-tsx-status.c2c-tsx--done { color: #14866d; }
.c2c-tsx--failed.c2c-tsx-dot { background: #b32424; }
.c2c-tsx-status.c2c-tsx--failed { color: #b32424; }
@keyframes c2c-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.7); }
}
.c2c-tsx-foot {
  margin: 12px 0 0 0; font-size: 11px; color: #72777d; text-align: center;
}
@media (max-width: 540px) {
  .c2c-tsx-item { flex-direction: column; align-items: flex-start; gap: 4px; }
}
</style>
